import { useMemo } from 'react';
import { toFix } from '../../../../helpers/commonHelper';
import { formatPlainDecimal, getAvailableBalanceForAsset, getAvailableNativeGasBalance, getReferencePrice } from '../placeOrder.utils';
import { getPairWalletFromBalance } from '../../../../services/wallet/linkedWallet.utils';
import { getSubmitDecimalPlaces } from "../buySellTab.utils.jsx";
export function useInsufficientBalance({
  selectedPair,
  formValues,
  selectedSide,
  isPerpetualMode,
  leverage,
  buyOrderBook,
  sellOrderBook,
  activePairBalance,
  selectedTradeWallet,
  gasFeeEstimate
}) {
  return useMemo(() => {
    if (!selectedPair) {
      return {
        insufficientBalance: false,
        balanceInsufficientTooltip: ''
      };
    }
    const qty = Number(formValues.quantity);
    const refPrice = getReferencePrice({
      selectedSide,
      sellOrderBook,
      buyOrderBook,
      limitPrice: formValues.price
    });
    const limitPrice = Number(formValues.price);
    const effectivePrice = limitPrice > 0 ? limitPrice : refPrice;
    if (!(effectivePrice > 0) || !(qty > 0)) {
      return {
        insufficientBalance: false,
        balanceInsufficientTooltip: ''
      };
    }
    const wallet = getPairWalletFromBalance(activePairBalance);
    const eps = 1e-10;
    if (isPerpetualMode) {
      const orderValue = qty * effectivePrice;
      const requiredMargin = orderValue / Math.max(leverage, 1);
      const available = getAvailableBalanceForAsset(wallet, selectedPair.baseCurrency);
      const base = String(selectedPair.baseCurrency || '').toUpperCase();
      if (requiredMargin > available + eps) {
        return {
          insufficientBalance: true,
          balanceInsufficientTooltip: `Insufficient ${base} margin: required ${toFix(requiredMargin, getSubmitDecimalPlaces(selectedPair.price_decimal))} ${base} exceeds available ${toFix(available, getSubmitDecimalPlaces(selectedPair.price_decimal))} ${base}.`
        };
      }
      return {
        insufficientBalance: false,
        balanceInsufficientTooltip: ''
      };
    }
    if (selectedSide === 0) {
      const base = String(selectedPair.baseCurrency || '').toUpperCase();
      const tradeAmount = effectivePrice * qty;
      const available = getAvailableBalanceForAsset(wallet, selectedPair.baseCurrency);
      if (tradeAmount > available + eps) {
        return {
          insufficientBalance: true,
          balanceInsufficientTooltip: `Insufficient ${base} balance: trade amount ${toFix(tradeAmount, Math.min(getSubmitDecimalPlaces(selectedPair.price_decimal) + getSubmitDecimalPlaces(selectedPair.quantity_decimal), 12))} ${base} exceeds available ${toFix(available, getSubmitDecimalPlaces(selectedPair.price_decimal))} ${base}.`
        };
      }
    } else {
      const other = String(selectedPair.otherCurrency || '').toUpperCase();
      const available = getAvailableBalanceForAsset(wallet, selectedPair.otherCurrency);
      if (qty > available + eps) {
        return {
          insufficientBalance: true,
          balanceInsufficientTooltip: `Insufficient ${other} balance: quantity ${toFix(qty, getSubmitDecimalPlaces(selectedPair.quantity_decimal))} ${other} exceeds available ${toFix(available, getSubmitDecimalPlaces(selectedPair.quantity_decimal))} ${other}.`
        };
      }
    }
    const feeAmt = Number(gasFeeEstimate?.amount);
    const feeCoin = String(gasFeeEstimate?.feeCoin || '').trim();
    if (Number.isFinite(feeAmt) && feeAmt > 0 && feeCoin) {
      const {
        available: gasAvailable,
        assetName
      } = getAvailableNativeGasBalance(activePairBalance?.wallet, feeCoin, selectedTradeWallet);
      if (feeAmt > gasAvailable + eps) {
        const label = String(assetName || feeCoin).toUpperCase();
        const requiredLabel = formatPlainDecimal(gasFeeEstimate.amount);
        const availableLabel = formatPlainDecimal(gasAvailable);
        return {
          insufficientBalance: true,
          balanceInsufficientTooltip: `Insufficient ${label} for gas fees: required ${requiredLabel} ${label} exceeds available ${availableLabel} ${label}.`
        };
      }
    }
    return {
      insufficientBalance: false,
      balanceInsufficientTooltip: ''
    };
  }, [activePairBalance, buyOrderBook, formValues.price, formValues.quantity, gasFeeEstimate, isPerpetualMode, leverage, selectedPair, selectedSide, selectedTradeWallet, sellOrderBook]);
}
export default useInsufficientBalance;